import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import './CreateTestRunPage.css';

interface Folder {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  level: number;
}

// Read URL params once at module level — no re-renders
const urlParams = new URLSearchParams(window.location.search);
const URL_FOLDER_ID = urlParams.get('folder_id') || '';
const URL_RUN_TYPE: 'SCENARIO' | 'TEST_CASE' =
  urlParams.get('type') === 'test_case' ? 'TEST_CASE' : 'SCENARIO';

const CreateTestRunPage: React.FC = () => {
  const navigate = useNavigate();
  const [allFolders, setAllFolders] = useState<Folder[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track whether run_type was changed manually (not from URL)
  const manualRunTypeChange = useRef(false);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project_id: '',
    folder_id: URL_FOLDER_ID,
    run_type: URL_RUN_TYPE as string,
    build_version: '',
    environment: ''
  });

  // Initial load — fetch folders for the run_type from URL
  useEffect(() => {
    fetchFolders(URL_RUN_TYPE);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When user manually changes run_type, reload folders and clear selections
  useEffect(() => {
    if (!manualRunTypeChange.current) return;
    manualRunTypeChange.current = false;
    const rt = formData.run_type as 'SCENARIO' | 'TEST_CASE';
    fetchFolders(rt, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.run_type]);

  const fetchFolders = async (runType: 'SCENARIO' | 'TEST_CASE', clearSelections = false) => {
    try {
      const folderType = runType === 'SCENARIO' ? 'test_scenario' : 'test_case';
      const folderList = await api.getFolders(folderType);
      setAllFolders(folderList);

      if (clearSelections) {
        setFormData(prev => ({ ...prev, project_id: '', folder_id: '' }));
      } else if (URL_FOLDER_ID) {
        // Auto-derive project from the preselected folder
        const projectId = findRootProject(folderList, URL_FOLDER_ID);
        setFormData(prev => ({ ...prev, project_id: projectId }));
      }

      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load folders');
      setLoading(false);
    }
  };

  const findRootProject = (folders: Folder[], folderId: string): string => {
    let current: Folder | undefined = folders.find(f => f.id === folderId);
    while (current) {
      if (current.level === 0) return current.id;
      const parentId: string | undefined = current.parent_id;
      current = parentId ? folders.find(f => f.id === parentId) : undefined;
    }
    return '';
  };

  // Derived — compute project folders and subfolders directly (no extra state/effects)
  const projectFolders = allFolders.filter(f => f.level === 0);

  const subfolders = formData.project_id
    ? allFolders.filter(f => {
        if (f.level === 0) return false;
        let current: Folder | undefined = f;
        while (current && current.parent_id) {
          const parentId: string | undefined = current.parent_id;
          if (parentId === formData.project_id) return true;
          current = allFolders.find(folder => folder.id === parentId);
        }
        return false;
      })
    : [];

  const getFolderPath = (folder: Folder): string => {
    const path: string[] = [folder.name];
    let current: Folder | undefined = folder;
    while (current && current.parent_id) {
      const parentId: string | undefined = current.parent_id;
      const parent: Folder | undefined = allFolders.find(f => f.id === parentId);
      if (parent && parent.level > 0) path.unshift(parent.name);
      current = parent;
    }
    return path.join(' / ');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === 'run_type') {
      manualRunTypeChange.current = true;
      setFormData(prev => ({ ...prev, run_type: value }));
    } else if (name === 'project_id') {
      setFormData(prev => ({ ...prev, project_id: value, folder_id: '' }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.project_id) { setError('Please select a project'); return; }
    if (!formData.folder_id) { setError('Please select a subfolder'); return; }
    try {
      setSaving(true);
      setError(null);
      const testRun = await api.createTestRun({
        name: formData.name,
        description: formData.description || undefined,
        folder_id: formData.folder_id,
        run_type: formData.run_type as 'SCENARIO' | 'TEST_CASE',
        build_version: formData.build_version || undefined,
        environment: formData.environment || undefined
      });
      navigate(`/test-runs/${testRun.id}`);
    } catch (err: any) {
      setError(err.message || 'Failed to create test run');
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="create-test-run-page"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="create-test-run-page">
      <div className="page-header">
        <button className="btn-back" onClick={() => navigate('/test-runs')}>
          ← Back to Test Runs
        </button>
      </div>

      <div className="form-container">
        <h1>Create Test Run</h1>
        <p className="subtitle">Create a new test run to execute test cases from a project</p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Test Run Name *</label>
            <input type="text" id="name" name="name" value={formData.name}
              onChange={handleChange} placeholder="e.g., Sprint 23 Regression" required />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea id="description" name="description" value={formData.description}
              onChange={handleChange} rows={3} placeholder="Optional description of this test run..." />
          </div>

          <div className="form-group">
            <label htmlFor="run_type">Run Type *</label>
            <select id="run_type" name="run_type" value={formData.run_type} onChange={handleChange} required>
              <option value="SCENARIO">Test Scenarios</option>
              <option value="TEST_CASE">Test Cases</option>
            </select>
            <small>Type of tests to include in this run (changes available folders)</small>
          </div>

          <div className="form-group">
            <label htmlFor="project_id">Project (Folder) *</label>
            <select id="project_id" name="project_id" value={formData.project_id} onChange={handleChange} required>
              <option value="">Select a project...</option>
              {projectFolders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <small>Select the top-level project folder</small>
          </div>

          <div className="form-group">
            <label htmlFor="folder_id">Subfolder *</label>
            <select id="folder_id" name="folder_id" value={formData.folder_id}
              onChange={handleChange} required disabled={!formData.project_id}>
              <option value="">
                {formData.project_id ? 'Select a subfolder...' : 'First select a project'}
              </option>
              {subfolders.map(f => <option key={f.id} value={f.id}>{getFolderPath(f)}</option>)}
            </select>
            <small>All test cases in this subfolder will be included in the test run</small>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="build_version">Build Version</label>
              <input type="text" id="build_version" name="build_version" value={formData.build_version}
                onChange={handleChange} placeholder="e.g., 1.2.3" />
            </div>
            <div className="form-group">
              <label htmlFor="environment">Environment</label>
              <select id="environment" name="environment" value={formData.environment} onChange={handleChange}>
                <option value="">Select environment...</option>
                <option value="Stage">Stage</option>
                <option value="Preprod">Preprod</option>
                <option value="PROD">PROD</option>
              </select>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={() => navigate('/test-runs')} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Creating...' : 'Create Test Run'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateTestRunPage;
